import { isDevAuth } from '@/lib/auth'
import type {
  ContractSummary,
  ContractDetail,
  AppNotification,
  ChecklistItem,
  ChecklistKind,
  FileKind,
  GoogleStatus,
  HealthResponse,
  Me,
  OppAttachment,
  OpportunityDetail,
  OpportunityListResponse,
  OpportunityQuery,
  OrgProfile,
  OrgStorage,
  PricingResponse,
  ProposalDetail,
  ProposalSummary,
  Pursuit,
  PursuitDetailResponse,
  PursuitFile,
  PursuitPatchInput,
  PursuitQuestion,
  PursuitStatus,
  PursuitVendor,
  QuestionsDraftEmailResponse,
  QuestionStatus,
  RunType,
  SamSyncStats,
  SignRequestSigner,
  SignRequestsResponse,
  StorageProvider,
  Template,
  VendorPatchInput,
  WorkflowRun,
} from '@/lib/types'

function resolveApiUrl(): string {
  // Use explicit env var if set at build time.
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL
  // Same-origin by default — the Vite dev server proxies /api and /health to
  // the Go API (http://localhost:8787), and prod serves both behind one host.
  return ''
}

const BASE_URL = resolveApiUrl()

export class ApiError extends Error {
  status: number
  body: unknown
  /** Error code from the API response body (e.g. "not_found"). */
  code?: string

  constructor(status: number, fallbackMessage: string, body?: unknown) {
    // Prefer the API's structured error message over the generic
    // "API error: 500" fallback so forms can display err.message directly.
    let resolvedMessage = fallbackMessage
    let resolvedCode: string | undefined
    if (body && typeof body === 'object') {
      const b = body as { message?: unknown; error?: unknown; code?: unknown }
      if (typeof b.message === 'string' && b.message.trim()) {
        resolvedMessage = b.message
      } else if (typeof b.error === 'string' && b.error.trim()) {
        resolvedMessage = b.error
      }
      if (typeof b.code === 'string' && b.code.trim()) {
        resolvedCode = b.code
      }
    }
    super(resolvedMessage)
    this.name = 'ApiError'
    this.status = status
    this.body = body
    this.code = resolvedCode
  }
}

// Active org for the X-Org-ID header. Owned by the Pinia session store
// (stores/session.ts), which pushes updates here — a plain module variable
// avoids a circular import between the store and this client.
let activeOrgId: string | null = null

export function setApiActiveOrg(orgId: string | null): void {
  activeOrgId = orgId
}

// Get auth token directly from Clerk's global JS instance.
// No Vue reactivity or wiring needed — works regardless of component mount order.
//
// We wait for a SESSION, not for clerk.loaded: Clerk restores the session
// early in its boot, long before the full `loaded` flag flips, and the
// token is all we need. The previous listener-based wait on `loaded` had a
// race (load completing between check and listener registration emitted no
// further events) that silently burned the entire 15s deadline on every
// fresh mobile load.
async function getClerkToken(): Promise<string | null> {
  if (isDevAuth) return null
  const deadline = Date.now() + 15000
  for (;;) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clerk = (window as any).Clerk
    if (clerk?.session) {
      return clerk.session.getToken()
    }
    if (clerk?.loaded && !clerk.session) {
      return null // definitively signed out — let the 401 redirect happen
    }
    if (Date.now() >= deadline) {
      return clerk?.session ? clerk.session.getToken() : null
    }
    await new Promise((r) => setTimeout(r, 50))
  }
}

/** Auth/tenancy headers shared by JSON, multipart, and blob requests. */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  const token = await getClerkToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (activeOrgId) {
    headers['X-Org-ID'] = activeOrgId
  }
  return headers
}

/** Read a failed response body and throw a structured ApiError. */
async function throwApiError(res: Response): Promise<never> {
  // Read the body as text once, then attempt to parse as JSON. Calling
  // res.json() then falling back to res.text() throws "body stream already
  // read" because fetch Response bodies can only be consumed once.
  const raw = await res.text()
  let errorBody: unknown = raw
  if (raw) {
    try {
      errorBody = JSON.parse(raw)
    } catch {
      // Not JSON — keep raw text body.
    }
  }
  throw new ApiError(
    res.status,
    `API error: ${res.status} ${res.statusText}`,
    errorBody,
  )
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers = await authHeaders()
  headers['Content-Type'] = 'application/json'

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    await throwApiError(res)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json()
}

/**
 * Multipart POST — no explicit Content-Type so the browser sets the
 * boundary itself. Auth headers still apply.
 */
async function requestMultipart<T>(path: string, form: FormData): Promise<T> {
  const headers = await authHeaders()
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: form,
  })
  if (!res.ok) {
    await throwApiError(res)
  }
  return res.json()
}

/**
 * Authenticated file download: plain <a href> can't carry the Authorization /
 * X-Org-ID headers, so fetch the stream into a Blob and click a temporary
 * object-URL anchor instead.
 */
/**
 * Open a document at its real URL.
 *
 * This used to fetch the bytes and show them from a blob: URL, because a new
 * tab can't send an Authorization header. That works until the user presses
 * refresh — the blob is gone and the tab is dead, which is exactly what
 * happens by accident on a long PDF. The server now accepts the same-origin
 * session cookie on these read-only GETs, so a plain URL works and reloads.
 */
function openInTab(path: string): void {
  const sep = path.includes('?') ? '&' : '?'
  const org = activeOrgId ? `${sep}org=${encodeURIComponent(activeOrgId)}` : ''
  const url = `${BASE_URL}${path}${org}`
  const win = window.open(url, '_blank')
  if (!win) {
    window.location.href = url // popup blocked
  }
}

async function postToDisk(path: string, body: unknown, filename: string): Promise<void> {
  const headers = await authHeaders()
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    await throwApiError(res)
  }
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
}

async function downloadToDisk(path: string, filename: string): Promise<void> {
  const headers = await authHeaders()
  const res = await fetch(`${BASE_URL}${path}`, { headers })
  if (!res.ok) {
    await throwApiError(res)
  }
  const blob = await res.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename || 'download'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Give the browser a beat to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
}

/** Build a query string from params, omitting empty/undefined values. */
function qs(
  params: Record<string, string | number | boolean | string[] | undefined | null>,
): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const v of value) if (v) search.append(key, v)
      continue
    }
    search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

export const api = {
  health: () => request<HealthResponse>('GET', '/health'),
  getMe: () => request<Me>('GET', '/api/v1/me'),
  ingestOpportunity: (ref: string) =>
    request<{ notice_id: string; title: string; solicitation: string; notices_found: number; enriching: boolean }>(
      'POST', '/api/v1/opportunities/ingest', { ref }),

  // Catalog
  listOpportunities: (query: OpportunityQuery = {}) =>
    request<OpportunityListResponse>('GET', `/api/v1/opportunities${qs({ ...query })}`),
  chatOpportunity: (noticeId: string, messages: { role: string; content: string }[]) =>
    request<{ reply: string }>('POST', `/api/v1/opportunities/${encodeURIComponent(noticeId)}/chat`, { messages }),
  dismissOpportunity: (noticeId: string, reason: string, note: string) =>
    request<{ status: string }>('POST', `/api/v1/opportunities/${encodeURIComponent(noticeId)}/dismiss`, { reason, note }),
  undismissOpportunity: (noticeId: string) =>
    request<{ status: string }>('DELETE', `/api/v1/opportunities/${encodeURIComponent(noticeId)}/dismiss`),
  getOpportunity: (noticeId: string) =>
    request<OpportunityDetail>('GET', `/api/v1/opportunities/${encodeURIComponent(noticeId)}`),

  // Pursuits
  userGoogleStatus: () =>
    request<{ status: string; google_email?: string }>('GET', '/api/v1/orgs/current/google/user'),
  userGoogleConnect: () =>
    request<{ url: string }>('GET', '/api/v1/orgs/current/google/user/connect'),
  userSignatureOptions: () =>
    request<{
      options: { email: string; name?: string; is_default: boolean; has_signature: boolean; preview?: string }[]
      chosen: string
      effective?: string
      needs_reconnect?: boolean
    }>('GET', '/api/v1/orgs/current/google/user/signature'),
  userSignatureSet: (sendAs: string) =>
    request<{ send_as: string }>('PUT', '/api/v1/orgs/current/google/user/signature', { send_as: sendAs }),
  userGoogleDisconnect: () =>
    request<{ status: string }>('DELETE', '/api/v1/orgs/current/google/user'),
  gmailCompose: (input: { mode: 'draft' | 'send'; to: string; cc?: string; subject: string; body: string; pursuit_id?: string }) =>
    request<{ status: string; draft_id?: string; gmail_message_id?: string; warning?: string }>('POST', '/api/v1/email/gmail', input),
  placesStatus: () =>
    request<{ configured: boolean }>('GET', '/api/v1/orgs/current/google/places'),
  placesSetKey: (apiKey: string) =>
    request<{ status: string }>('PUT', '/api/v1/orgs/current/google/places', { api_key: apiKey }),
  retellStatus: () =>
    request<{ configured: boolean; voice: string; numbers: { id: string; clerk_user_id: string; e164: string; member_name: string }[] }>(
      'GET', '/api/v1/orgs/current/retell'),
  retellSetKey: (api_key: string) =>
    request<{ configured: boolean }>('PUT', '/api/v1/orgs/current/retell', { api_key }),
  retellAvailableNumbers: () =>
    request<{ owned: { e164: string; nickname: string; assigned_to: string }[] }>(
      'GET', '/api/v1/orgs/current/retell/available-numbers'),
  retellAssignNumber: (input: { member_id: string; e164?: string; area_code?: string }) =>
    request<{ e164: string }>('POST', '/api/v1/orgs/current/retell/numbers', input),
  retellVoices: () =>
    request<{ voices: { voice_id: string; voice_name: string; provider: string; gender: string; accent: string; age: string }[] }>(
      'GET', '/api/v1/orgs/current/retell/voices'),
  retellSetVoice: (voice: string) =>
    request<{ voice: string }>('PUT', '/api/v1/orgs/current/retell/voice', { voice }),
  uploadLogo: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return requestMultipart<{ status: string; mime: string }>('/api/v1/orgs/current/logo', fd)
  },
  deleteLogo: () => request<{ status: string }>('DELETE', '/api/v1/orgs/current/logo'),
  /**
   * Whether a company logo has been uploaded. The endpoint serves the image
   * itself and 404s when there isn't one, so asking is a request — but it is
   * the same request the settings page's <img> already makes, and the answer
   * is only used to explain a checkbox.
   */
  hasOrgLogo: async () => {
    try {
      const res = await fetch('/api/v1/orgs/current/logo', { credentials: 'same-origin' })
      return res.ok
    } catch {
      return false
    }
  },
  getFeatures: () =>
    request<{ features: Record<string, boolean>; platform_admin: boolean }>('GET', '/api/v1/orgs/current/features'),
  adminListOrgs: () =>
    request<{ organizations: { id: string; name: string; members: number; features: Record<string, boolean>; retell_configured: boolean; places_configured: boolean; google_configured: boolean; storage: string; voice: string }[]; registry: { key: string; title: string; description: string; cost?: string; default: boolean }[] }>(
      'GET', '/api/v1/admin/orgs'),
  /**
   * Re-run the LLM passes on notices whose documents ingested but whose AI
   * fields never landed (typically a provider outage). Costs real tokens, so
   * it is manual and system-admin only.
   */
  adminAIRepair: (input: {
    window_hours: number
    limit: number
    dry_run: boolean
    trigger_automation?: boolean
    force_reevaluate?: boolean
  }) =>
    request<{ workflow_id: string; run_id: string; dry_run: boolean }>(
      'POST', '/api/v1/admin/ai-repair', input),
  adminAIRepairStatus: () =>
    request<{
      status: string
      started_at?: string
      finished_at?: string
      result?: { candidates: number; repaired: number; failed: number; sourced: number; dry_run: boolean }
    }>('GET', '/api/v1/admin/ai-repair'),
  adminSetKey: (orgId: string, provider: 'retell' | 'places', apiKey: string) =>
    request<{ status: string }>('PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/keys/${provider}`, { api_key: apiKey }),
  adminSetGoogleClient: (orgId: string, clientId: string, clientSecret: string) =>
    request<{ status: string }>('PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/google-client`, { client_id: clientId, client_secret: clientSecret }),
  adminSetStorage: (orgId: string, provider: string) =>
    request<{ provider: string }>('PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/storage`, { provider }),
  adminSetVoice: (orgId: string, voice: string) =>
    request<{ voice: string }>('PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/voice`, { voice }),
  adminSetFeature: (orgId: string, key: string, enabled: boolean) =>
    request<{ enabled: boolean }>('PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/features/${encodeURIComponent(key)}`, { enabled }),
  listTasks: (params?: Record<string, string>) =>
    request<{ tasks: import('./types').Task[] }>(
      'GET', `/api/v1/tasks${params && Object.keys(params).length ? '?' + new URLSearchParams(params) : ''}`),
  createTask: (input: {
    title: string; notes?: string; priority?: string; due_date?: string
    assignee_member_id?: string; notice_id?: string; pursuit_id?: string; contract_id?: string
  }) => request<{ id: string }>('POST', '/api/v1/tasks', input),
  patchTask: (id: string, input: Record<string, unknown>) =>
    request<{ task: import('./types').Task }>('PATCH', `/api/v1/tasks/${encodeURIComponent(id)}`, input),
  adminAutosourcePreview: (orgId: string) =>
    request<{ preview: { considered: number; evaluated: number; qualified: number; skipped?: string[] } }>(
      'POST', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/autosource/preview`),
  adminGetDraftIdentity: (orgId: string) =>
    request<{ chosen: string; members: { id: string; name: string; email: string; connected: boolean }[] }>(
      'GET', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/draft-identity`),
  adminSetDraftIdentity: (orgId: string, memberId: string) =>
    request<{ member_id: string }>(
      'PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/draft-identity`, { member_id: memberId }),
  adminGetAutosource: (orgId: string) =>
    request<{
      settings: import('./types').AutosourceSettings
      members: { id: string; name: string; email: string; role: string }[]
    }>('GET', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/autosource`),
  adminSetAutosource: (orgId: string, settings: import('./types').AutosourceSettings) =>
    request<{ settings: import('./types').AutosourceSettings }>(
      'PUT', `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/autosource`, settings),
  listPrompts: () =>
    request<{ prompts: { key: string; title: string; description: string; vars: string[]; default: string; override?: string; updated_at?: string }[]; var_docs: Record<string, string> }>(
      'GET', '/api/v1/orgs/current/prompts'),
  setPrompt: (key: string, content: string) =>
    request<{ status: string }>('PUT', `/api/v1/orgs/current/prompts/${encodeURIComponent(key)}`, { content }),
  outreachScriptPreview: (pursuitId: string, askText: string) =>
    request<{ script: string; greeting: string; verify_script: string; verify_greeting: string }>('POST', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/outreach-script-preview`, { ask_text: askText }),
  createCampaign: (pursuitId: string, input: { ask_text: string; onesheet_file_id: string; vendor_ids: string[] }) =>
    request<{ id: string; status: string; contacts: unknown[] }>(
      'POST', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/outreach-campaigns`, input),
  listCampaigns: (pursuitId: string) =>
    request<{ campaigns: { id: string; status: string; ask_text: string; contacts: { id: string; vendor_name: string; phone: string; status: string; contact_name?: string; contact_email?: string; email_confirmed: boolean; call_summary?: string }[] }[] }>(
      'GET', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/outreach-campaigns`),
  launchCampaign: (cid: string) =>
    request<{ status: string }>('POST', `/api/v1/outreach-campaigns/${encodeURIComponent(cid)}/launch`),
  cancelCampaign: (cid: string) =>
    request<{ status: string }>('POST', `/api/v1/outreach-campaigns/${encodeURIComponent(cid)}/cancel`),
  retellTestCall: (to: string) =>
    request<{ call_id: string; status: string }>('POST', '/api/v1/orgs/current/retell/test-call', { to }),
  listMembers: () =>
    request<{ members: import('./types').OrgMember[] }>('GET', '/api/v1/orgs/current/members'),
  putMemberRole: (memberId: string, role: string) =>
    request<{ status: string }>('PUT', `/api/v1/orgs/current/members/${encodeURIComponent(memberId)}/role`, { role }),
  createPursuit: (input: { notice_id: string; template_key: string; assign_to?: string }) =>
    request<Pursuit>('POST', '/api/v1/pursuits', input),
  listPursuits: (status?: string) =>
    request<{ pursuits: Pursuit[] }>('GET', `/api/v1/pursuits${qs({ status })}`),
  getPursuit: (id: string) =>
    request<PursuitDetailResponse>('GET', `/api/v1/pursuits/${encodeURIComponent(id)}`),
  patchPursuit: (id: string, patch: PursuitPatchInput) =>
    request<Pursuit>('PATCH', `/api/v1/pursuits/${encodeURIComponent(id)}`, patch),

  /**
   * One status change across many pursuits. Partial success is normal — a
   * pursuit held by a running workflow comes back in `skipped`, not as an
   * error for the whole batch.
   */
  bulkPursuitStatus: (input: {
    ids: string[]
    status: PursuitStatus
    no_bid_reason?: string
    lost_reason?: string
    lost_note?: string
  }) =>
    request<{ updated: Pursuit[]; skipped: { id: string; reason: string }[] }>(
      'POST', '/api/v1/pursuits/bulk-status', input),

  // Templates
  deleteProposal: (pid: string) =>
    request<{ status: string }>('DELETE', `/api/v1/proposals/${encodeURIComponent(pid)}`),
  listTemplates: () => request<{ templates: Template[] }>('GET', '/api/v1/templates'),

  // Notifications
  listNotifications: (unreadOnly = false) =>
    request<{ notifications: AppNotification[] }>(
      'GET',
      `/api/v1/notifications${unreadOnly ? '?unread=true' : ''}`,
    ),
  /** Pass "all" as the id to mark every notification read. */
  markNotificationRead: (id: string) =>
    request<unknown>('POST', `/api/v1/notifications/${encodeURIComponent(id)}/read`),

  // Org settings
  getOrgProfile: () => request<OrgProfile>('GET', '/api/v1/orgs/current/profile'),
  putOrgProfile: (profile: OrgProfile) =>
    request<OrgProfile>('PUT', '/api/v1/orgs/current/profile', profile),
  getOrgStorage: () => request<OrgStorage>('GET', '/api/v1/orgs/current/storage'),
  putOrgStorage: (provider: StorageProvider) =>
    request<OrgStorage>('PUT', '/api/v1/orgs/current/storage', { provider }),

  // Google connection
  getGoogleStatus: () => request<GoogleStatus>('GET', '/api/v1/orgs/current/google'),
  setGoogleClient: (input: { client_id: string; client_secret: string }) =>
    request<unknown>('POST', '/api/v1/orgs/current/google/client', input),
  getGoogleConnectUrl: () =>
    request<{ url: string }>('GET', '/api/v1/orgs/current/google/connect'),
  disconnectGoogle: () => request<unknown>('DELETE', '/api/v1/orgs/current/google'),

  // Opportunity enrichment & cached attachments
  enrichOpportunity: (noticeId: string, opts?: { force?: boolean }) =>
    request<{ status: string }>(
      'POST',
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/enrich`,
      opts?.force ? { force: true } : {},
    ),
  listAttachments: (noticeId: string) =>
    request<{ attachments: OppAttachment[] }>(
      'GET',
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/attachments`,
    ),
  getAttachmentText: (noticeId: string, url: string) =>
    request<{ text: string }>(
      'GET',
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/attachments/text${qs({ url })}`,
    ),
  /**
   * Chosen documents for a solicitation, as one named archive. An empty
   * selection means every current document.
   */
  downloadAttachmentsZip: (noticeId: string, urls: string[] = [], filename = 'documents.zip') =>
    postToDisk(
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/attachments/zip`,
      { urls },
      filename,
    ),
  viewAttachment: (noticeId: string, att: { url: string }) =>
    openInTab(
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/attachments/download${qs({ url: att.url, disposition: 'inline' })}`,
    ),
  downloadAttachment: (noticeId: string, att: { url: string; filename: string }) =>
    downloadToDisk(
      `/api/v1/opportunities/${encodeURIComponent(noticeId)}/attachments/download${qs({ url: att.url })}`,
      att.filename,
    ),

  // Pursuit files
  listPursuitFiles: (pursuitId: string) =>
    request<{ files: PursuitFile[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/files`,
    ),
  uploadPursuitFile: (pursuitId: string, file: File, kind: FileKind) => {
    const form = new FormData()
    form.set('file', file)
    form.set('kind', kind)
    return requestMultipart<PursuitFile>(
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/files`,
      form,
    )
  },
  /** Remove a file from a pursuit — scoped to that pursuit, and audited. */
  deletePursuitFile: (pursuitId: string, fileId: string) =>
    request<{ status: string; filename: string }>(
      'DELETE',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/files/${encodeURIComponent(fileId)}`,
    ),
  downloadPursuitFile: (file: { id: string; filename: string }) =>
    downloadToDisk(`/api/v1/files/${encodeURIComponent(file.id)}/download`, file.filename),
  /** Open a stored file in the browser's own viewer, at a reloadable URL. */
  // The vendor BOOK — suppliers as records of their own, distinct from the
  // pursuit-scoped vendor slate above.
  listIUID: (params?: Record<string, string>) =>
    request<{ items: import('./types').IUIDItem[] }>('GET', `/api/v1/iuid${qs(params ?? {})}`),
  createIUID: (input: Record<string, unknown>) =>
    request<{ ids: string[]; count: number }>('POST', '/api/v1/iuid', input),
  previewIUID: (input: Record<string, unknown>) =>
    request<{
      uii: string
      payload: string
      svg: string
      serials: string[]
      /** True size of the finished plate, in inches. */
      width_in: number
      height_in: number
    }>('POST', '/api/v1/iuid/preview', input),
  patchIUID: (id: string, input: Record<string, unknown>) =>
    request<{ status: string }>('PATCH', `/api/v1/iuid/${encodeURIComponent(id)}`, input),
  iuidLabelUrl: (id: string, format: 'svg' | 'png' = 'svg') =>
    `/api/v1/iuid/${encodeURIComponent(id)}/label?format=${format}`,
  downloadIUIDLabel: (id: string, uii: string, format: 'svg' | 'png' = 'svg') =>
    downloadToDisk(`/api/v1/iuid/${encodeURIComponent(id)}/label?format=${format}`,
      `IUID_${uii}.${format}`),
  iuidSerials: (count = 1) =>
    request<{ settings: import('./types').IUIDSerialSettings; next: string[] }>(
      'GET', `/api/v1/iuid/serials?count=${count}`),
  setIUIDSerials: (input: Record<string, unknown>) =>
    request<{ settings: import('./types').IUIDSerialSettings; next: string[] }>(
      'PUT', '/api/v1/iuid/serials', input),
  listVendorBook: (params?: { q?: string; relationship?: string }) =>
    request<{ vendors: import('./types').VendorProfile[] }>('GET', `/api/v1/vendors${qs(params ?? {})}`),
  getVendorProfile: (id: string) =>
    request<{ vendor: import('./types').VendorProfile }>('GET', `/api/v1/vendors/${encodeURIComponent(id)}`),
  patchVendorProfile: (id: string, input: Record<string, unknown>) =>
    request<{ vendor: import('./types').VendorProfile }>('PATCH', `/api/v1/vendors/${encodeURIComponent(id)}`, input),
  requestOwnership: (pursuitId: string, note: string) =>
    request<{ request: import('./types').OwnershipRequest }>(
      'POST', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/ownership-request`, { note }),
  listOwnershipRequests: () =>
    request<{
      requests: import('./types').OwnershipRequest[]
      me_member_id: string
      window_secs: number
      server_time: string
    }>('GET', '/api/v1/ownership-requests'),
  decideOwnership: (id: string, action: 'approve' | 'deny' | 'withdraw') =>
    request<{ request: import('./types').OwnershipRequest }>(
      'POST', `/api/v1/ownership-requests/${encodeURIComponent(id)}/${action}`),
  listVendorPursuits: (id: string) =>
    request<{ pursuits: import('./types').VendorPursuit[] }>(
      'GET', `/api/v1/vendors/${encodeURIComponent(id)}/pursuits`),
  crawlVendorProfile: (id: string) =>
    request<{ profile: Record<string, unknown> }>('POST', `/api/v1/vendors/${encodeURIComponent(id)}/crawl`),
  autosourceAnalysis: (noticeId: string) =>
    request<any>('GET', `/api/v1/opportunities/${encodeURIComponent(noticeId)}/autosource-analysis`),
  viewFile: (file: { id: string }) =>
    openInTab(`/api/v1/files/${encodeURIComponent(file.id)}/download?disposition=inline`),

  // Contracts (post-award)
  listContracts: () =>
    request<{ contracts: ContractSummary[] }>('GET', '/api/v1/contracts'),
  getContract: (cid: string) =>
    request<ContractDetail>('GET', `/api/v1/contracts/${encodeURIComponent(cid)}`),
  createContract: (input: { award_number: string; title: string; pursuit_id?: string; agency?: string; vehicle_type?: string; awarded_on?: string; total_value?: number; periods?: { label: string; starts_on: string; ends_on: string }[] }) =>
    request<{ id: string }>('POST', '/api/v1/contracts', input),
  patchContract: (cid: string, patch: Record<string, unknown>) =>
    request<{ status: string }>('PATCH', `/api/v1/contracts/${encodeURIComponent(cid)}`, patch),
  addContractPeriod: (cid: string, p: { label: string; starts_on: string; ends_on: string }) =>
    request<{ id: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/periods`, p),
  deleteContractPeriod: (cid: string, pid: string) =>
    request<{ status: string }>('DELETE', `/api/v1/contracts/${encodeURIComponent(cid)}/periods/${encodeURIComponent(pid)}`),
  addTaskOrder: (cid: string, t: { order_number: string; description?: string; amount?: number; due_on?: string }) =>
    request<{ id: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/task-orders`, t),
  patchTaskOrder: (cid: string, toid: string, status: string) =>
    request<{ status: string }>('PATCH', `/api/v1/contracts/${encodeURIComponent(cid)}/task-orders/${encodeURIComponent(toid)}`, { status }),
  createInvoice: (cid: string, input: { invoice_number?: string; invoice_date?: string; memo?: string; task_order_id?: string; line_items: { description: string; qty: number; unit_price: number }[] }) =>
    request<{ id: string; invoice_number: string; total: number }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/invoices`, input),
  listInvoices: (cid: string) =>
    request<{ invoices: { id: string; invoice_number: string; invoice_date: string; total: number; memo?: string }[] }>(
      'GET', `/api/v1/contracts/${encodeURIComponent(cid)}/invoices`),
  downloadInvoicePDF: (cid: string, iid: string, filename: string) =>
    downloadToDisk(`/api/v1/contracts/${encodeURIComponent(cid)}/invoices/${encodeURIComponent(iid)}/pdf`, filename),
  addContractTx: (cid: string, t: { kind: string; amount: number; occurred_on: string; memo?: string; reference?: string; task_order_id?: string }) =>
    request<{ id: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/transactions`, t),
  settleInvoice: (cid: string, txid: string, date?: string) =>
    request<{ status: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/transactions/${encodeURIComponent(txid)}/settle`, { date }),
  voidContractTx: (cid: string, txid: string, note: string) =>
    request<{ status: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/transactions/${encodeURIComponent(txid)}/void`, { note }),
  addLiability: (cid: string, input: { label: string; principal: number; rate_pct?: number; rate_period?: string; incurred_on?: string; memo?: string; cash_draw?: boolean; vendor_id?: string }) =>
    request<{ id: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/liabilities`, input),
  payLiability: (cid: string, lid: string, input: { amount: number; date?: string; note?: string }) =>
    request<{ status: string; closed: boolean }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/liabilities/${encodeURIComponent(lid)}/pay`, input),
  listOrgVendors: () =>
    request<{ vendors: { id: string; name: string }[] }>('GET', '/api/v1/vendors'),
  closeLiability: (cid: string, lid: string, input: { date?: string; amount?: number; note?: string; log_payment?: boolean }) =>
    request<{ status: string }>('POST', `/api/v1/contracts/${encodeURIComponent(cid)}/liabilities/${encodeURIComponent(lid)}/close`, input),

  // Workflow runs (M3)
  startRun: (pursuitId: string, type: RunType) =>
    request<{ run_id: string }>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/runs`,
      { type },
    ),
  listRuns: (pursuitId: string) =>
    request<{ runs: WorkflowRun[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/runs`,
    ),

  // Questions (M3)
  listQuestions: (pursuitId: string) =>
    request<{ questions: PursuitQuestion[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/questions`,
    ),
  createQuestion: (pursuitId: string, question: string) =>
    request<{ id: string }>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/questions`,
      { question },
    ),
  patchQuestion: (
    pursuitId: string,
    qid: string,
    patch: { question?: string; status?: QuestionStatus; answer?: string },
  ) =>
    request<unknown>(
      'PATCH',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/questions/${encodeURIComponent(qid)}`,
      patch,
    ),
  deleteQuestion: (pursuitId: string, qid: string) =>
    request<unknown>(
      'DELETE',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/questions/${encodeURIComponent(qid)}`,
    ),
  draftQuestionsEmail: (pursuitId: string) =>
    request<QuestionsDraftEmailResponse>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/questions/draft-email`,
    ),

  // Vendors & pricing (M4)
  listVendors: (pursuitId: string) =>
    request<{ vendors: PursuitVendor[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/vendors`,
    ),
  addVendor: (
    pursuitId: string,
    input: { name: string; city?: string; state?: string; phone?: string; email?: string; url?: string },
  ) =>
    request<unknown>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/vendors`,
      input,
    ),
  vendorEmailDrafts: (pursuitId: string, input?: { onesheet_file_id?: string; vendor_ids?: string[] }) =>
    request<{ drafts_created: number; onesheet: string; skipped: number }>(
      'POST', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/vendors/email-drafts`, input ?? {}),
  removeVendor: (pursuitId: string, pvId: string) =>
    request<{ status: string }>('DELETE', `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/vendors/${encodeURIComponent(pvId)}`),
  patchVendor: (pursuitId: string, pvId: string, patch: VendorPatchInput) =>
    request<unknown>(
      'PATCH',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/vendors/${encodeURIComponent(pvId)}`,
      patch,
    ),
  getPricing: (pursuitId: string) =>
    request<PricingResponse>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/pricing`,
    ),

  // Checklist (M4.5)
  listChecklist: (pursuitId: string) =>
    request<{ items: ChecklistItem[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/checklist`,
    ),
  createChecklistItem: (
    pursuitId: string,
    input: { label: string; detail?: string; kind: ChecklistKind; required?: boolean; position?: number },
  ) =>
    request<{ id: string }>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/checklist`,
      input,
    ),
  patchChecklistItem: (
    pursuitId: string,
    itemId: string,
    patch: { label?: string; detail?: string; done?: boolean; required?: boolean; position?: number; file_id?: string },
  ) =>
    request<unknown>(
      'PATCH',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/checklist/${encodeURIComponent(itemId)}`,
      patch,
    ),
  deleteChecklistItem: (pursuitId: string, itemId: string) =>
    request<unknown>(
      'DELETE',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/checklist/${encodeURIComponent(itemId)}`,
    ),

  // Proposals (M4.5)
  listProposals: (pursuitId: string) =>
    request<{ proposals: ProposalSummary[] }>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/proposals`,
    ),
  createProposal: (pursuitId: string, title: string) =>
    request<{ id: string }>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/proposals`,
      { title },
    ),
  downloadProposalTex: (pid: string, filename: string) =>
    downloadToDisk(`/api/v1/proposals/${encodeURIComponent(pid)}/tex`, filename),
  downloadProposalMarkdown: (pid: string, filename: string) =>
    downloadToDisk(`/api/v1/proposals/${encodeURIComponent(pid)}/markdown?raw=1`, filename),
  getProposalMarkdown: (pid: string) =>
    request<{ markdown: string }>('GET', `/api/v1/proposals/${encodeURIComponent(pid)}/markdown`),
  getProposal: (pid: string) =>
    request<ProposalDetail>('GET', `/api/v1/proposals/${encodeURIComponent(pid)}`),
  putProposal: (pid: string, patch: { title?: string; content?: unknown; source_md?: string }) =>
    request<unknown>('PUT', `/api/v1/proposals/${encodeURIComponent(pid)}`, patch),
  renderProposal: (pid: string) =>
    request<{ file_id: string; version: number }>(
      'POST',
      `/api/v1/proposals/${encodeURIComponent(pid)}/render`,
    ),

  // Sign requests (M4.5)
  listSignRequests: (pursuitId: string) =>
    request<SignRequestsResponse>(
      'GET',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/sign-requests`,
    ),
  createSignRequest: (
    pursuitId: string,
    input: { file_id: string; signers: SignRequestSigner[]; checklist_item_id?: string },
  ) =>
    request<{ sign_url: string }>(
      'POST',
      `/api/v1/pursuits/${encodeURIComponent(pursuitId)}/sign-requests`,
      input,
    ),
  refreshSignRequest: (srid: string) =>
    request<{ status: string; signed_file_id?: string }>(
      'POST',
      `/api/v1/sign-requests/${encodeURIComponent(srid)}/refresh`,
    ),

  // Platform admin (M5)
  getSamSyncStats: () => request<SamSyncStats>('GET', '/api/v1/admin/sam-sync'),
}
